// Companion micro-surface implementing DEC-039.
// Guardrails:
// - HS17: no email/push transport touched. wren-brain calls only.
// - HS16: sensor + visual-baseline required at deploy time (this file's changes trigger verify-ui).
// - INV-14: every AI number cites its data. WhyTapModal renders event-ID chips.
// - DEC-019: never claim medical safety. Greeting must not include prescriptive language.
// - No TTS. No proactive notifications. Explicit user tap opens Why modal.

import type { Aggregate, AggregateEvidence } from '../../../supabase/functions/_shared/aggregateCore'
export { validAggregate as validateCompanionAggregate } from '../../../supabase/functions/_shared/aggregateCore'
import { askBrain } from '@/lib/aiBrain'
import { supabase } from '@/lib/supabase'

export interface CompanionScope { userId: string; familyId: string; personId: string }
export interface CompanionMeter { kind: string; label: string; actual_min: number; target_min: number }
export interface EvidenceEvent {
  id: string; event_type: string; occurred_at: string; server_sequence: number
  supersedes_event_id: string | null
}
export interface EvidenceWindow { start: string; end: string; events: EvidenceEvent[] }
export interface CompanionAnswer {
  text: string
  eventIds: string[]
  confidence: 'record-backed' | 'insufficient'
  window: { start: string; end: string } | null
  limitation: string
  ai: boolean
  aggregates?: Record<string, Aggregate>
  evidence?: AggregateEvidence
}
interface Choice { text: string; event_ids: string[] }

const TTL = 5 * 60_000
const LIMIT = 300
const cache = new Map<string, { expires: number; promise: Promise<CompanionAnswer> }>()
const CHANGE = 'wren:companion-preference'
let generation = 0
const revokedLocally = new Set<string>() // revocation wins even if persistence fails

// Preference only; no household prose, event IDs or tokens in persistent storage.
function preferenceKey(userId: string, familyId: string) {
  return `wren:companion_enabled:${userId}:${familyId}`
}
export function companionEnabled(userId?: string | null, familyId?: string | null): boolean {
  if (!userId || !familyId || revokedLocally.has(preferenceKey(userId, familyId))) return false
  try { return localStorage.getItem(preferenceKey(userId, familyId)) === 'true' } catch { return false }
}
export function clearCompanionCache() { generation++; cache.clear() }
export function setCompanionEnabled(userId: string, familyId: string, enabled: boolean): boolean {
  clearCompanionCache()
  const key = preferenceKey(userId, familyId)
  if (!enabled) revokedLocally.add(key)
  try {
    localStorage.setItem(key, String(enabled))
    if (enabled) revokedLocally.delete(key)
    window.dispatchEvent(new Event(CHANGE))
    return true
  } catch {
    window.dispatchEvent(new Event(CHANGE))
    return false
  }
}
export function subscribeCompanion(listener: () => void) {
  const changed = () => { clearCompanionCache(); listener() }
  window.addEventListener('storage', changed)
  window.addEventListener(CHANGE, changed)
  return () => {
    window.removeEventListener('storage', changed)
    window.removeEventListener(CHANGE, changed)
  }
}
// Auth boundaries invalidate even pending responses, not just settled cache entries.
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT' || event === 'SIGNED_IN' || event === 'USER_UPDATED') clearCompanionCache()
})

function fallback(text = 'I do not have enough verified context for this yet.'): CompanionAnswer {
  return { text, eventIds: [], confidence: 'insufficient', window: null, limitation: 'No AI claim shown.', ai: false }
}

/** Fresh RLS reads, never the unscoped local mirror. Corrections outside the interval
 * must also suppress their ancestors. A full/truncated page is not evidence of a total.
 * This is a bounded observation window, not a new snapshot/projection authority. */
export async function loadCompanionEvidence(scope: CompanionScope, now = new Date()): Promise<EvidenceWindow> {
  const end = now.toISOString()
  const start = new Date(now.getTime() - 24 * 60 * 60_000).toISOString()
  const { data, error } = await supabase.from('events')
    .select('id, event_type, occurred_at, server_sequence, supersedes_event_id')
    .eq('family_id', scope.familyId).eq('person_id', scope.personId)
    .gte('occurred_at', start).lte('occurred_at', end)
    .order('occurred_at', { ascending: false }).order('server_sequence', { ascending: false }).limit(LIMIT + 1)
  if (error || !data || data.length > LIMIT) throw new Error('Evidence unavailable or truncated')
  const rows = data as EvidenceEvent[]
  if (!rows.length) return { start, end, events: [] }
  const corrections = await supabase.from('events').select('supersedes_event_id')
    .eq('family_id', scope.familyId).eq('person_id', scope.personId)
    .in('supersedes_event_id', rows.map((r) => r.id)).limit(LIMIT + 1)
  if (corrections.error || !corrections.data || corrections.data.length > LIMIT) throw new Error('Correction coverage unavailable')
  const superseded = new Set(corrections.data.map((r) => r.supersedes_event_id))
  return { start, end, events: rows.filter((r) => !superseded.has(r.id)) }
}

const eventLabels: Record<string, string> = {
  'early_care.feeding.recorded': 'feeding', 'early_care.feeding.corrected': 'feeding',
  'early_care.diaper.recorded': 'diaper', 'early_care.diaper.corrected': 'diaper',
  'early_care.sleep.started': 'sleep start', 'early_care.sleep.ended': 'sleep end',
  'note.created': 'note',
}

function greetingChoices(evidence: EvidenceWindow): Choice[] {
  const latest = evidence.events[0]
  const label = latest && eventLabels[latest.event_type]
  if (!label) return []
  const hour = new Date(evidence.end).getHours()
  const daypart = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening'
  // Name substituted locally only after validation; no adult name sent to the model.
  return [`Good ${daypart}`, 'Hello', 'Welcome back'].map((salutation) => ({
    text: `${salutation}, CAREGIVER; the latest record in this window is a ${label} entry.`,
    event_ids: [latest.id],
  }))
}

export function pairedSleepEvidence(evidence: EvidenceWindow): { minutes: number; eventIds: string[] } | null {
  const sleep = evidence.events.filter((e) => /^early_care\.sleep\.(started|ended)$/.test(e.event_type))
    .sort((a, b) => a.occurred_at.localeCompare(b.occurred_at) || a.server_sequence - b.server_sequence)
  // Refuse incomplete/ambiguous pairing rather than explaining an incorrect meter.
  if (!sleep.length || sleep.length % 2) return null
  let hours = 0
  for (let i = 0; i < sleep.length; i += 2) {
    const a = sleep[i], b = sleep[i + 1]
    if (!a.event_type.endsWith('.started') || !b.event_type.endsWith('.ended')) return null
    const duration = (Date.parse(b.occurred_at) - Date.parse(a.occurred_at)) / 3_600_000
    if (!(duration > 0 && duration < 24)) return null
    hours += duration
  }
  // Match the existing rhythm meter's rounding, not a different calculation.
  return { minutes: Math.round((Math.round(hours * 10) / 10) * 60), eventIds: sleep.map((e) => e.id) }
}

export function meterChoices(meter: CompanionMeter, evidence: EvidenceWindow): Choice[] {
  // Other meters derive from rhythm_completions, whose schema has no event_id.
  // Neither those row IDs nor generic AAP source slugs are contributing event IDs.
  if (meter.kind !== 'sleep_adequacy') return []
  const paired = pairedSleepEvidence(evidence)
  if (!paired || paired.minutes !== meter.actual_min) return []
  return [{
    text: `The paired sleep records in this window account for ${paired.minutes} logged minutes after meter rounding.`,
    event_ids: paired.eventIds,
  }]
}

/** Generated text is untrusted. Exact verified choices prevent the common failure
 * of plausible advice/false numbers decorated with genuine but unrelated ID chips.
 * V1 is intentionally constrained authorship, not free-form medical reasoning. */
export function validateCompanionReply(text: string | null, choices: Choice[]): Choice | null {
  if (!text || text.length > 20_000) return null
  try {
    const parsed: unknown = JSON.parse(text.replace(/^```(?:json)?\s*|\s*```$/g, '').trim())
    if (!parsed || typeof parsed !== 'object') return null
    const p = parsed as Partial<Choice>
    if (typeof p.text !== 'string' || !Array.isArray(p.event_ids) || !p.event_ids.length) return null
    return choices.find((c) => c.text === p.text && c.event_ids.length === p.event_ids!.length
      && c.event_ids.every((id, i) => id === p.event_ids![i])) ?? null
  } catch { return null }
}

async function run(scope: CompanionScope, meter: CompanionMeter | undefined, epoch: number): Promise<CompanionAnswer> {
  const evidence = await loadCompanionEvidence(scope)
  if (epoch !== generation || !companionEnabled(scope.userId, scope.familyId)) return fallback()
  const choices = meter ? meterChoices(meter, evidence) : greetingChoices(evidence)
  if (!choices.length) return fallback(meter
    ? 'I cannot trace this meter to a complete matching set of event records yet.'
    : 'I do not have enough recent record context to greet you personally yet.')
  const result = await askBrain({
    task: meter ? 'reasoning' : 'chat', personId: scope.personId,
    responseMode: meter ? 'companion_sleep' : 'companion_greeting',
    // Existing edge contract has user_message, NOT caller-controlled system messages.
    userMessage: `You are Wren. ${meter ? 'Explain the logged portion of this rhythm meter.' : 'Write ONE sentence greeting CAREGIVER by name using the current time and latest record.'}
No preamble, exclamation, advice, diagnosis, or safety claims. Do not infer current bodily state from a log.
Select and return exactly one of the following evidence-verified JSON objects, with no additional text. If context is insufficient return null.
Use only the exact choices here. External corpus sources are not household event evidence.
Context: ${JSON.stringify({ window: { start: evidence.start, end: evidence.end }, meter: meter ?? null })}
Choices: ${JSON.stringify(choices)}`,
  })
  if (result.error || result.capExceeded) return fallback('Wren is unavailable right now; no AI claim is shown.')
  const choice = validateCompanionReply(result.text, choices)
  if (!choice) return fallback('Wren could not produce a verifiable response; no AI claim is shown.')
  return {
    text: choice.text, eventIds: [...choice.event_ids], confidence: 'record-backed',
    window: { start: evidence.start, end: evidence.end }, ai: result.tier !== null,
    aggregates: result.aggregates ?? undefined,
    evidence: meter && result.aggregates?.sleep_minutes_last_24h?.status === 'known'
      ? result.aggregates.sleep_minutes_last_24h.evidence : undefined,
    limitation: meter
      ? 'Explains logged sleep, not the target or medical adequacy. Only complete pairs inside this interval; rounding matches the meter. Unsynced or later-corrected records may be missing.'
      : 'Latest confirmed record in this interval, not a claim about current bodily state. Unsynced or later-corrected records may be missing.',
  }
}

async function request(scope: CompanionScope, meter?: CompanionMeter): Promise<CompanionAnswer | null> {
  if (!companionEnabled(scope.userId, scope.familyId)) return null
  const epoch = generation
  try {
    // Revalidate live membership even before returning cached prose (INV-9).
    const { data, error } = await supabase.from('family_members').select('user_id')
      .eq('family_id', scope.familyId).eq('user_id', scope.userId).eq('status', 'active').maybeSingle()
    if (error || !data) { clearCompanionCache(); return null }
    if (epoch !== generation || !companionEnabled(scope.userId, scope.familyId)) return null
    const key = JSON.stringify([scope.userId, scope.familyId, scope.personId, meter ?? 'greeting'])
    let entry = cache.get(key)
    if (!entry || entry.expires <= Date.now()) {
      // Deduplicate in-flight StrictMode/remount requests; failures are cached too.
      if (cache.size >= 24) cache.delete(cache.keys().next().value!)
      entry = { expires: Date.now() + TTL, promise: run(scope, meter, epoch).catch(() => fallback('Verified records are unavailable right now.')) }
      cache.set(key, entry)
    }
    const answer = await entry.promise
    return epoch === generation && companionEnabled(scope.userId, scope.familyId) ? answer : null
  } catch { return fallback('Verified records are unavailable right now.') }
}

export async function getCompanionGreeting(scope: CompanionScope, caregiverName: string): Promise<CompanionAnswer | null> {
  const answer = await request(scope)
  if (!answer) return null
  const name = caregiverName.trim().split(/\s+/)[0] || 'there'
  return { ...answer, text: answer.text.replace('CAREGIVER', () => name) }
}
export function getCompanionWhy(scope: CompanionScope, meter: CompanionMeter) { return request(scope, meter) }
export function companionEventHref(eventId: string) { return `/timeline/event/${encodeURIComponent(eventId)}` }
